import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import PurpelBtn from "../../commanComponents/buttons/PurpelBtn";
import PaginationButton from "../../commanComponents/buttons/PaginationButton";
import AddContractorUsers from "./AddContractorUsers";
import SelectBox from "../../commanComponents/selects/SelectBox";

import { useQuery, useMutation } from "react-query";
import { communication } from "../../constants/apis/communication";
import toast from "react-hot-toast";
import useDebounce from "../../constants/helpers/useDebounce";
import Loader from "../../commanComponents/Loader";
import SelectMine from "../../commanComponents/SelectMine";
import { PaginationContext } from "../../constants/context/PaginationProvider";
import { dateFormatDayMonthYear } from "../../constants/helpers/helperFunctions";
import { getUserDetails } from "../../constants/helpers/getUserDetails";
import BorderButton from "../../commanComponents/buttons/BorderButton";

function ListContractorUsers() {
  const navigate = useNavigate();
  const { userManagementPage, setUserManagementPage, globalSearchString, setCustomSearchPlaceholder, setCustomSearchTooltip } = useContext(PaginationContext);
  const debouncedSearchTerm = useDebounce(globalSearchString, 1000, setUserManagementPage);
  const pageLimit = process.env.REACT_APP_PUBLIC_LIMIT;
  const [showModal, setShowModal] = useState({ isOpen: false, userId: null, isView: false });
  const [activeTab, setActiveTab] = useState("departmental");
  const [confirmRoleChange, setConfirmRoleChange] = useState({ isOpen: false, ele: null, role: null });
  const [selectedMine, setSelectedMine] = useState(getUserDetails()?.mineId || "");

  React.useEffect(() => {
    if (activeTab === "departmental") {
      setCustomSearchPlaceholder("Search by Employee No., Employee Name, Mobile, Position Name, Department, Employee Group, Employee Subgroup, Gender");
      setCustomSearchTooltip("Search by Employee No., Employee Name, Mobile, Position Name, Department, Employee Group, Employee Subgroup, Gender");
    } else {
      setCustomSearchPlaceholder("Search by User Name, Phone Number, Designation, Contract From Date, Contract To Date");
      setCustomSearchTooltip("Search by User Name, Phone Number, Designation, Contract From Date, Contract To Date");
    }

    return () => {
      setCustomSearchPlaceholder("");
      setCustomSearchTooltip("");
    };
  }, [activeTab, setCustomSearchPlaceholder, setCustomSearchTooltip]);

  const {
    data: employee,
    isFetching: employeeListFetching,
    refetch: refetchEmployeeList,
  } = useQuery(
    ["getEmployeeList", userManagementPage, debouncedSearchTerm, selectedMine],
    () => communication?.getUserList({ searchString: debouncedSearchTerm, page: userManagementPage, mineId: selectedMine }),
    {
      enabled: activeTab === "departmental",
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data?.data?.status === "JWT_INVALID") {
          toast.error(`Failed: ${data?.data?.message}`);
        } else if (data?.data?.status === "FAILED") {
          toast.error(`Failed: ${data?.data?.message}`);
        }
      },
      onError: (error) => {
        toast.error(`Failed: ${error?.message}`);
      },
    }
  );

  // Query for Contractor Users
  const {
    data: contractorUserData,
    isFetching: isContractorFetching,
    refetch: refetchContractor,
  } = useQuery(
    ["getContractorUsers", userManagementPage, debouncedSearchTerm, selectedMine],
    () =>
      communication?.getContractorUsers({
        searchString: debouncedSearchTerm,
        page: userManagementPage,
        mineId: selectedMine,
      }),
    {
      enabled: activeTab === "contractor",
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data?.data?.status === "JWT_INVALID") {
          toast.error(`Failed: ${data?.data?.message}`);
        } else if (data?.data?.status === "FAILED") {
          toast.error(`Failed: ${data?.data?.message}`);
        }
      },
      onError: (error) => {
        toast.error(`Failed: ${error?.message}`);
      },
    }
  );

  const isFetching = activeTab === "contractor" ? isContractorFetching : employeeListFetching;
  const refetch = activeTab === "contractor" ? refetchContractor : refetchEmployeeList;
  const totalPages = activeTab === "contractor" ? contractorUserData?.data?.totalPages : employee?.data?.employee?.totalPages;

  const user = getUserDetails();
  const userRoles = Array.isArray(user?.designation) ? user.designation.map((d) => d.toLowerCase()) : typeof user?.designation === "string" ? [user.designation.toLowerCase()] : [];
  const isSuperAdmin = userRoles.includes("superadmin");
  const updateRoleMutation = useMutation(
    async ({ ele, role }) => {
      console.log("ele", ele);
      console.log("role", role);
      const dataToSend = {
        id: ele?._id,
        type: role,
        mineId: selectedMine,
      };

      const response = await communication.updateUserDesignation(dataToSend);
      return response?.data;
    },
    {
      onSuccess: (data) => {
        if (data?.status === "SUCCESS") {
          toast.success(data?.message || "Role updated successfully");
          refetch();
        } else {
          toast.error(data?.message || "Failed to update role");
        }
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to assign role");
      },
    }
  );

  return (
    <div>
      {(isFetching || updateRoleMutation.isLoading) && <Loader />}
      <div className="pageWrapper">
        <div className="searchWrapper d-flex flex-column flex-md-row gap-3 justify-content-between align-items-start align-items-md-center">
          <div className="d-flex gap-2 flex-wrap" style={{ alignItems: "center", width: "100%" }}>
            <BorderButton
              text="Departmental Users"
              onClick={() => {
                setActiveTab("departmental");
                setUserManagementPage(1);
              }}
              isActive={activeTab === "departmental"}
              style={{ flex: 1, minWidth: "160px", justifyContent: "center" }}
            />
            <BorderButton
              text="Contractor Users"
              onClick={() => {
                setActiveTab("contractor");
                setUserManagementPage(1);
              }}
              isActive={activeTab === "contractor"}
              style={{ flex: 1, minWidth: "160px", justifyContent: "center" }}
            />
          </div>
          <div className="d-flex align-items-center justify-content-start justify-content-md-end gap-2 w-100 mt-2 mt-md-0">
            {activeTab === "contractor" && <PurpelBtn text={"+ Add"} onClick={() => setShowModal({ isOpen: true, userId: null, isView: false })} />}
            <SelectMine selectedMine={selectedMine} onChange={(e) => setSelectedMine(e.target.value)} />
            <PaginationButton setPage={setUserManagementPage} page={userManagementPage} totalPages={totalPages} pageName={"userManagement"} />
          </div>
        </div>

        <div className="tableWrapper comman-box-shadow" style={{ position: "relative", minHeight: "400px", height: "68vh", overflowX: "auto", overflowY: "auto", marginTop: "10px" }}>
          <table>
            <thead>
              {activeTab === "departmental" ? (
                <tr>
                  <th className="text-center col_80px">Sr. No.</th>
                  <th className="col_150px">Employee No.</th>
                  <th className="col_350px">Employee Name </th>
                  <th className="col_200px">Mobile</th>
                  <th className="col_400px">Position Name</th>
                  <th className="col_300px">Department</th>
                  <th className="col_200px">Employee Group</th>
                  <th className="col_200px">Employee Subgroup</th>
                  <th className="col_150px">Gender</th>
                  <th className="text-center row-fix-to-last col_350px">Action</th>
                </tr>
              ) : (
                <tr>
                  <th className="text-center col_80px">Sr. No.</th>

                  <th className="col_200px">User Name</th>
                  <th className="col_150px">Phone Number</th>
                  <th className="col_150px">Designation</th>
                  <th className="col_200px">Contract From Date</th>
                  <th className="col_200px">Contract To Date</th>
                  {/* <th className="col_150px">Employee Status</th> */}
                  <th className="text-center row-fix-to-last col_220px">Action</th>
                </tr>
              )}
            </thead>
            <tbody>
              {activeTab === "departmental"
                ? employee?.data?.employee?.users?.map((ele, index) => {
                    return (
                      <tr key={ele?._id || index} className={index % 2 === 0 ? "skyBlueTrBg" : ""}>
                        <td className="text-center">{(userManagementPage - 1) * pageLimit + (index + 1)}</td>
                        <td className="">{ele?.employeeNumber || ele?.userId || "--"}</td>
                        <td className="">{ele?.name || "--"}</td>
                        <td className="">{ele?.phoneNumber || "--"}</td>
                        <td className="">{ele?.positionName || "--"}</td>
                        <td className="">{ele?.department || "--"}</td>
                        <td className="">{ele?.employee_group || "--"}</td>
                        <td className="">{ele?.employeeSubgroup || "--"}</td>
                        <td className="">{ele?.gender || "--"}</td>
                        <td className="text-center d-flex gap-2 justify-content-center align-items-center row-fix-to-last" style={{ width: "350px" }}>
                          <SelectBox
                            value={
                              Array.isArray(ele?.designation)
                                ? ele.designation.map((d) => ({ label: d.replace(/-/g, " ").replace(/supervisor/gi, "incharge"), value: d }))
                                : ele?.designation
                                  ? { label: ele.designation.replace(/-/g, " ").replace(/supervisor/gi, "incharge"), value: ele.designation }
                                  : null
                            }
                            isMulti={true}
                            onChange={(selectedOptions) => {
                              const newRoles = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                              setConfirmRoleChange({ isOpen: true, ele, role: newRoles });
                            }}
                            isDisabled={updateRoleMutation.isLoading}
                            options={[
                              { label: "Operator", value: "operator" },
                              { label: "Incharge", value: "supervisor" },
                              { label: "Maintenance Incharge", value: "maintenance-supervisor" },
                              // { label: "HOD (Mining)", value: "hod-mining" },
                              { label: "HOD (Mechanical)", value: "hod-mechanical" },
                              ...(isSuperAdmin ? [{ label: "Mine Admin", value: "mine-admin" }] : []),
                            ]}
                            placeholder="Select"
                            height="auto"
                            style={{ minWidth: "200px", fontSize: "12px", minHeight: "36px" }}
                          />
                          <button
                            onClick={() => navigate(`/panel/user-logs?userId=${ele?._id}`, { state: { designationLogs: ele?.designationLogs || [] } })}
                            className="premiumActionBtn view"
                            title="View"
                          >
                            <span>View logs</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                : contractorUserData?.data?.data?.map((ele, index) => {
                    return (
                      <tr key={ele?._id || index} className={index % 2 === 0 ? "skyBlueTrBg" : ""}>
                        <td className="text-center">{(userManagementPage - 1) * pageLimit + (index + 1)}</td>
                        {/* {isSuperAdmin && <td className="text-capitalize">{ele?.mineName || "--"}</td>}
                        {isSuperAdmin && <td>{ele?.mineId || "--"}</td>} */}

                        <td className="">{ele?.name || "--"}</td>
                        <td>{ele?.phoneNumber || "--"}</td>
                        <td className="">{Array.isArray(ele?.designation) ? ele.designation.join(", ") : ele?.designation || "--"}</td>
                        <td className="">{dateFormatDayMonthYear(ele?.from) || "--"}</td>
                        <td className="">{dateFormatDayMonthYear(ele?.to) || "--"}</td>
                        {/* <td className="">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: ele?.employment_status ? "#38a169" : "#e53e3e",
                                flexShrink: 0,
                              }}
                            ></span>
                            <span
                              style={{
                                color: ele?.employment_status ? "#276749" : "#9b2c2c",
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              {ele?.employment_status ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td> */}
                        <td className="text-center d-flex gap-3 justify-content-center align-items-center row-fix-to-last">
                          <button onClick={() => setShowModal({ isOpen: true, userId: ele?._id, isView: true })} className="premiumActionBtn view" title="View">
                            <span>View</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <button onClick={() => setShowModal({ isOpen: true, userId: ele?._id, isView: false })} className="premiumActionBtn edit" title="Edit">
                            <span>Edit</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>
      {showModal.isOpen && (
        <AddContractorUsers
          userId={showModal.userId}
          isViewMode={showModal.isView}
          onClose={() => {
            setShowModal({ isOpen: false, userId: null, isView: false });
            refetch();
          }}
        />
      )}

      {confirmRoleChange.isOpen && (
        <div className="premiumModalOverlay" onClick={() => setConfirmRoleChange({ isOpen: false, ele: null, role: null })}>
          <div className="premiumModalContent" style={{ maxWidth: "400px", textAlign: "center", padding: "30px 20px" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "16px", color: "#1a202c", fontSize: "20px", fontWeight: "700" }}>Confirm Change</h3>
            <p style={{ color: "#4a5568", marginBottom: "24px", fontSize: "15px", lineHeight: "1.5" }}>
              Are you sure you want to change the designation of <strong style={{ color: "#2d3748" }}>{confirmRoleChange.ele?.name}</strong>
              {confirmRoleChange.ele?.designation && confirmRoleChange.ele?.designation.length > 0 ? (
                <>
                  {" "}
                  from{" "}
                  <strong style={{ color: "#e53e3e", textTransform: "capitalize" }}>
                    {(Array.isArray(confirmRoleChange.ele?.designation) ? confirmRoleChange.ele.designation : [confirmRoleChange.ele?.designation]).join(", ").replace(/-/g, " ").replace(/supervisor/gi, "incharge")}
                  </strong>
                </>
              ) : (
                ""
              )}{" "}
              to{" "}
              <strong style={{ color: "#2f3299", textTransform: "capitalize" }}>
                {(Array.isArray(confirmRoleChange.role) ? confirmRoleChange.role : [confirmRoleChange.role]).join(", ").replace(/-/g, " ").replace(/supervisor/gi, "incharge")}
              </strong>
              ?
            </p>
            <div className="d-flex gap-3 justify-content-center mt-2">
              <button type="button" className="premiumBtnCancel" onClick={() => setConfirmRoleChange({ isOpen: false, ele: null, role: null })}>
                Cancel
              </button>
              <button
                type="button"
                className="premiumBtnSubmit"
                onClick={() => {
                  updateRoleMutation.mutate({ ele: confirmRoleChange.ele, role: confirmRoleChange.role });
                  setConfirmRoleChange({ isOpen: false, ele: null, role: null });
                }}
              >
                Yes, Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ListContractorUsers;
